/**
 * Shapes and labels for the SSN file viewer.
 *
 * The file is a dictionary of SSN -> the (name, employee id) pairs filed under it,
 * so one row is one number and the entries under it are what the matching reads.
 * The backend counts the whole file and hands over one page of rows; full numbers
 * arrive only when an operator asks for them, which is why every row carries a
 * mask and `ssn` is null until then.
 */

/** What a head of the major job's own file says - the shape `ssn_file_facts`
 * returns. `error` set means storage could not be asked. */
export interface SsnFileInfo {
  vendor: string;
  key: string;
  filename: string;
  exists: boolean;
  size_bytes: number | null;
  last_modified: string | null;
  empty: boolean;
  major_job_id?: number | null;
  major_job_name?: string;
  error?: string | null;
}

export type SsnFileState =
  | "all"
  | "several_names"
  | "no_name"
  | "no_id"
  | "invalid_ssn"
  | "shared_id";

export interface SsnFileEntry {
  employee_name: string;
  employee_id: string;
  [key: string]: unknown;
}

export interface SsnFileRowDto {
  /** The number itself - null unless the reader asked to see it. */
  ssn: string | null;
  ssn_masked: string;
  names: string[];
  ids: string[];
  entries: SsnFileEntry[];
  entry_count: number;
  flags: Exclude<SsnFileState, "all">[];
}

export interface SsnFileCounts {
  ssns: number;
  entries: number;
  names: number;
  shared_ids: number;
  several_names: number;
  no_name: number;
  no_id: number;
  invalid_ssn: number;
  shared_id: number;
}

export interface SsnFileRecordsResponse {
  file: SsnFileInfo;
  counts: SsnFileCounts;
  id_shapes: string[];
  records: SsnFileRowDto[];
  total_matched: number;
  offset: number;
  limit: number;
  has_more: boolean;
  revealed: boolean;
  can_reveal: boolean;
  generated_at: string;
}

export interface SsnFileQuery {
  state?: SsnFileState;
  q?: string;
  limit?: number;
  offset?: number;
  reveal?: boolean;
}

export const EMPTY_SSN_FILE_COUNTS: SsnFileCounts = {
  ssns: 0,
  entries: 0,
  names: 0,
  shared_ids: 0,
  several_names: 0,
  no_name: 0,
  no_id: 0,
  invalid_ssn: 0,
  shared_id: 0,
};

/** What each defect means, in the words the panel uses elsewhere. */
export const SSN_FLAG_LABELS: Record<Exclude<SsnFileState, "all">, string> = {
  several_names: "Several names",
  no_name: "No name",
  no_id: "No id",
  invalid_ssn: "Not an SSN",
  shared_id: "Shared name + id",
};

export const SSN_FLAG_TOOLTIPS: Record<Exclude<SsnFileState, "all">, string> = {
  several_names:
    "Two different people are filed under this number - the match finds both",
  no_name: "An entry with no name: nothing to match a folder against by name",
  no_id: "An entry with no employee id: the match has nothing to resolve to",
  invalid_ssn: "The key is not a number in the XXX-XX-XXXX shape the bots write",
  shared_id:
    "This name and employee id are filed under another SSN as well - the bot keeps only one of them",
};

export const SSN_FILE_FILTERS: Array<{ value: SsnFileState; label: string }> = [
  { value: "all", label: "All" },
  { value: "several_names", label: SSN_FLAG_LABELS.several_names },
  { value: "no_name", label: SSN_FLAG_LABELS.no_name },
  { value: "no_id", label: SSN_FLAG_LABELS.no_id },
  { value: "invalid_ssn", label: SSN_FLAG_LABELS.invalid_ssn },
  { value: "shared_id", label: SSN_FLAG_LABELS.shared_id },
];

export function buildSsnFileQuery(params: SsnFileQuery): string {
  const search = new URLSearchParams();
  if (params.state && params.state !== "all") search.append("state", params.state);
  if (params.q) search.append("q", params.q);
  if (params.limit != null) search.append("limit", String(params.limit));
  if (params.offset) search.append("offset", String(params.offset));
  if (params.reveal) search.append("reveal", "1");
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "the last run wrote no SSN for anybody" - said the same way everywhere. */
export const EMPTY_SSN_FILE_MESSAGE =
  "The SSN file is empty - the last run wrote no SSN for anybody. This major job's " +
  "upload jobs, and the upload jobs that read it as their Source SSN file, match by " +
  "name until it is rebuilt; the next SSN run collects it again.";
